import type { NavigationGuardReturn, RouteLocation } from "vue-router";

import { initTracking } from "@/analytics/init";
import { openConnection } from "@/backend/Connection";
import { startBackendSession } from "@/backend/backendProvider";
import { getUserTeams } from "@/backend/http/userAdmin";
import { setSentryUser } from "@/error/sentry";
import type { AuthUser, TechnicalUser } from "@/model/user";
import { useCompanySettingsStore } from "@/store/companySettings";
import { useLoadingStore } from "@/store/loading";
import { useUserStore } from "@/store/user";
import { isCloud } from "@/utils/env/Environment";
import { initFeatureToggles } from "@/utils/env/featureToggles";
import { setupInactivityLogout } from "@/utils/inactivityLogout";
import { setLastCompanyCookie } from "@/utils/lastCompanyCookie";

import { isSmallScreen } from "./guards/utils";
import { currentRoute, goToLogin, goToLogout } from "./navigation";

type Target = RouteLocation["query"] & { path: string };

export async function establishSession(partialUser: TechnicalUser): Promise<void> {
  setLastLoginAt();

  const user = { ...partialUser, teams: await getUserTeams(partialUser.id) };
  useUserStore().technicalUser = user;
  setSentryUser(user.company, user.id);
  if (isCloud) setLastCompanyCookie(user.company);
  const authUser = await connect(user);
  initTracking(authUser, user);
  initInactivityTimeout();
}

export function loginTarget(target: Target): NavigationGuardReturn {
  // Returning users on small screens go to mobile info page
  if (isSmallScreen() && isCloud && localStorage.getItem("device") !== "desktop") {
    return { name: "mobile" };
  }

  const query = { ...target } as RouteLocation["query"];
  delete query.session;
  delete query.board;
  delete query.team;
  delete query.path;
  delete query.reload;
  return { path: target.path || "/", query };
}

async function connect(user: TechnicalUser): Promise<AuthUser> {
  const time = Math.floor(Date.now() / 1000);
  const connectionName = `${user.company}:${user.id}:${time}`;
  const [authUser] = await Promise.all([
    useLoadingStore().load("user", useUserStore().loadCurrentUser()),
    useLoadingStore().load("featureToggles", initFeatureToggles(user.company)),
    useLoadingStore().load("connect", connectBackend(connectionName)),
  ]);
  return authUser;
}

async function connectBackend(connectionName: string) {
  const connection = await openConnection(
    () => currentRoute().path,
    { login: goToLogin, logout: goToLogout },
    connectionName,
  );
  await startBackendSession(connection);
}

function initInactivityTimeout() {
  const timeoutMinutes = useCompanySettingsStore().autoLogoutAfter;
  setupInactivityLogout({ timeoutMinutes, onTimeout: () => void goToLogout("inactivity") });
}

function setLastLoginAt() {
  localStorage.setItem("lastLoginAt", Date.now().toString());
}
